import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, App, Button, Card, DatePicker, Input, Modal, Radio, Segmented, Select, Space, Spin, Tooltip, Typography, type InputRef } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, DesktopOutlined, EditOutlined, ExclamationCircleOutlined, ExperimentOutlined, MobileOutlined, SaveOutlined, SendOutlined, TeamOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import type { Audience, CampaignItem, EmailDesign, MergeContext } from '@crm/shared';
import { defaultEmailDesign, designFromHtml, emailDesignProblems, emptyAudience, projectEmailStyle, renderMergeTags } from '@crm/shared';
import { palette } from '../../app/theme';
import { useDocumentTitle } from '../../utils/document-head';
import { AgentSelect } from '../../components/AgentSelect';
import { EmojiPicker } from '../../components/EmojiPicker';
import { ProjectSelect } from '../../components/ProjectSelect';
import { useProjectScope } from '../../features/projects/scope';
import { EmailDesigner, previewHtml, projectBrand } from '../../components/email-designer/EmailDesigner';
import { EmailPreview } from '../../components/email-designer/EmailPreview';
import { AiEmailButtons } from '../../components/email-designer/AiEmailButtons';
import { errorMessage } from '../../api/client';
import { useAudiencePeople, useAudiencePreview, useCampaign, useCreateCampaign, useCreateTemplate, useSendCampaign, useTemplates, useTestSend, useUpdateCampaign } from '../../features/campaigns/api';
import { useAgents } from '../../features/email/api';
import { useContact } from '../../features/contacts/api';
import { useSettings } from '../../features/settings/api';
import { useAuth } from '../../auth/AuthProvider';
import { AudienceEditor } from './AudienceEditor';
import { CampaignStatsPage } from './CampaignStatsPage';
import { PageSkeleton, RecordError } from '../../components/states';
import { fmtDateTime, plural } from '../../utils/format';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AUTOSAVE_MS = 1200;
/** Shown in "tags filled in" previews when the audience has nobody yet. */
const SAMPLE_CONTACT: NonNullable<MergeContext['contact']> = { firstName: 'Alex', lastName: 'Taylor', email: 'alex@example.com', jobTitle: 'Marketing Lead', organization: { name: 'Example Ltd' }, fields: {} };
const MERGE_CHIPS = [
  { label: 'First name', tag: '{{first_name|there}}' },
  { label: 'Last name', tag: '{{last_name}}' },
  { label: 'Full name', tag: '{{full_name}}' },
  { label: 'Company', tag: '{{organization_name}}' },
  { label: 'Email', tag: '{{email}}' },
];

interface Draft {
  name: string;
  agentId: string | null;
  templateId: string | null;
  projectId: string | null;
  subject: string;
  design: EmailDesign;
  audience: Audience;
  requireVerified: boolean;
  includeUnconfirmed: boolean;
  replyTo: string;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function CampaignEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const { user } = useAuth();
  const campaign = useCampaign(isNew ? undefined : id);
  const settings = useSettings();
  const scope = useProjectScope();
  /** project labels are only sent while projects are on (the API refuses them otherwise) */
  const payloadOf = (d: Draft) => campaignPayload(d, scope.enabled);
  const templates = useTemplates();
  const agents = useAgents();
  const create = useCreateCampaign();
  const update = useUpdateCampaign();
  const send = useSendCampaign();
  const test = useTestSend();
  const createTemplate = useCreateTemplate();
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [sendAt, setSendAt] = useState<Dayjs | null>(null);
  const [testTo, setTestTo] = useState('');
  const [reviewDevice, setReviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [templateName, setTemplateName] = useState<string | null>(null);
  const savedJson = useRef<string | null>(null);
  const savedId = useRef<string | null>(isNew ? null : id!);
  const creating = useRef<Promise<CampaignItem | null> | null>(null);
  const subjectRef = useRef<InputRef>(null);
  const preheaderRef = useRef<InputRef>(null);
  const lastField = useRef<'subject' | 'preheader'>('subject');

  useDocumentTitle(draft ? `${draft.name || 'Untitled campaign'} · Campaigns` : 'Campaigns');

  // load once: an existing campaign, or a fresh design in the workspace's branding
  useEffect(() => {
    if (draft) return;
    const brand = {
      ...(settings.data ? { logoUrl: settings.data.brandLogoFileId ? `/api/files/${settings.data.brandLogoFileId}` : '', footerText: settings.data.companyName ? `You are receiving this email from ${settings.data.companyName}.` : '' } : {}),
      // inside a project, new emails start in its branding (logo, button and link colour)
      ...projectEmailStyle(scope.current),
    };
    if (!isNew && campaign.data) {
      const c = campaign.data;
      const loaded: Draft = { name: c.name, agentId: c.agentId, templateId: c.templateId, projectId: c.projectId, subject: c.subject, design: c.design ?? designFromHtml(c.html, brand), audience: { ...emptyAudience(), ...c.audience, include: { ...emptyAudience().include, ...c.audience.include } }, requireVerified: c.requireVerified, includeUnconfirmed: c.includeUnconfirmed, replyTo: c.replyTo ?? '' };
      // older campaigns get their HTML wrapped in a design, but nothing is saved until the user changes something
      savedJson.current = JSON.stringify(payloadOf(loaded));
      setDraft(loaded);
      // a scheduled campaign opens on its current time, so the main button reschedules rather than sending now
      if (c.status === 'SCHEDULED' && c.scheduledAt) {
        setWhen('later');
        setSendAt(dayjs(c.scheduledAt));
      }
    } else if (isNew && (settings.data || settings.isError) && scope.ready) {
      const fresh: Draft = { name: '', agentId: null, templateId: null, projectId: scope.current?.id ?? null, subject: '', design: defaultEmailDesign(brand), audience: emptyAudience(), requireVerified: false, includeUnconfirmed: false, replyTo: '' };
      // a new campaign is created on its first change, so opening and leaving the page leaves no empty draft behind
      savedJson.current = JSON.stringify(payloadOf(fresh));
      setDraft(fresh);
    }
  }, [draft, isNew, campaign.data, settings.data, settings.isError, scope.ready, scope.current]);

  useEffect(() => {
    if (user?.email && !testTo) setTestTo(user.email);
  }, [user?.email, testTo]);

  const status = campaign.data?.status;
  const editable = isNew || !campaign.data || status === 'DRAFT' || status === 'SCHEDULED';
  const verified = { requireVerified: draft?.requireVerified ?? false, includeUnconfirmed: draft?.includeUnconfirmed ?? false };
  const preview = useAudiencePreview(draft?.audience ?? emptyAudience(), verified);
  const firstRecipient = useAudiencePeople(draft?.audience ?? emptyAudience(), verified, { kind: 'eligible', page: 1, pageSize: 1 });
  const sampleContact = useContact(firstRecipient.data?.items[0]?.id);
  const sample: MergeContext['contact'] = sampleContact.data ? { ...sampleContact.data, organization: sampleContact.data.organization ?? null, fields: (sampleContact.data.fields ?? {}) as Record<string, unknown> } : SAMPLE_CONTACT;
  const agent = useMemo(() => agents.data?.find((a) => a.id === draft?.agentId) ?? null, [agents.data, draft?.agentId]);

  const save = async (d: Draft): Promise<CampaignItem | null> => {
    const payload = payloadOf(d);
    const json = JSON.stringify(payload);
    if (savedId.current && json === savedJson.current && campaign.data) return campaign.data;
    if (!savedId.current && creating.current) {
      // a create is already on its way; save these edits as an update once it lands
      await creating.current;
      return save(d);
    }
    setSaveState('saving');
    try {
      let saved: CampaignItem;
      if (!savedId.current) {
        // one create at a time; later edits wait for it and then save as an update
        creating.current = create.mutateAsync(payload).then((c) => {
          savedId.current = c.id;
          navigate(`/campaigns/${c.id}`, { replace: true });
          return c;
        });
        saved = (await creating.current)!;
        creating.current = null;
      } else {
        saved = await update.mutateAsync({ id: savedId.current, ...payload });
      }
      savedJson.current = json;
      setSaveState('saved');
      return saved;
    } catch (e) {
      creating.current = null;
      setSaveState('error');
      message.error(errorMessage(e));
      return null;
    }
  };

  // autosave a moment after the last change
  const latest = useRef(draft);
  latest.current = draft;
  useEffect(() => {
    if (!draft || !editable) return;
    if (JSON.stringify(payloadOf(draft)) === savedJson.current) return;
    const t = window.setTimeout(() => latest.current && void save(latest.current), AUTOSAVE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, editable]);

  if (!isNew && campaign.isError) return <RecordError error={campaign.error} what="Campaign" backTo="/campaigns" backLabel="Back to campaigns" onRetry={campaign.refetch} />;
  if (!isNew && campaign.data && !editable) return <CampaignStatsPage campaign={campaign.data} />;
  if (!draft) return <PageSkeleton />;

  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const eligible = preview.data?.eligible ?? 0;
  const designProblems = emailDesignProblems(draft.design);
  const replyToInvalid = !!draft.replyTo.trim() && !EMAIL_RE.test(draft.replyTo.trim());
  const problems = [
    ...(!draft.subject.trim() ? ['The subject line is empty.'] : []),
    ...(replyToInvalid ? ['The reply-to address is not a valid email.'] : []),
    ...(!agent ? ['Choose who the email is from.'] : !agent.isActive ? [`${agent.name} is inactive. Choose an active sender.`] : []),
    ...designProblems,
    ...(preview.isError ? ['Could not count recipients. Check the Recipients step.'] : !preview.isFetching && eligible < 1 ? ["There's nobody to send to. Add groups, people or email addresses on the Recipients step."] : []),
  ];
  const scheduleInvalid = when === 'later' && (!sendAt || sendAt.isBefore(dayjs().add(1, 'minute')));
  const testList = testTo.split(/[\s,;]+/).filter(Boolean);
  const testInvalid = !testList.length || testList.length > 5 || testList.some((e) => !EMAIL_RE.test(e));

  const insertTag = (tag: string, field = lastField.current) => {
    const el = (field === 'subject' ? subjectRef : preheaderRef).current?.input;
    const current = field === 'subject' ? draft.subject : draft.design.preheader;
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + tag + current.slice(end);
    if (field === 'subject') set({ subject: next });
    else set({ design: { ...draft.design, preheader: next } });
    window.requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + tag.length, start + tag.length);
    });
  };

  const pickTemplate = (v: string | undefined) => {
    const t = templates.data?.find((x) => x.id === v);
    if (!t) return set({ templateId: null });
    const apply = () => set({ templateId: t.id, subject: t.subject, design: withProjectLogo(t.design ?? designFromHtml(t.html, draft.design.style), draft.projectId), agentId: draft.agentId ?? t.agentId });
    modal.confirm({ title: `Use "${t.name}"?`, content: 'This replaces the subject and the email design you have now.', okText: 'Use template', onOk: apply });
  };

  /** The project's logo goes at the top of the email (a project without a logo leaves the current one). */
  const withProjectLogo = (design: EmailDesign, projectId: string | null): EmailDesign => ({ ...design, style: { ...design.style, ...projectEmailStyle(scope.byId(projectId)) } });
  const pickProject = (projectId: string | null) => set({ projectId, design: withProjectLogo(draft.design, projectId) });

  const doSend = async () => {
    const saved = await save(draft);
    if (!saved) return;
    try {
      const c = await send.mutateAsync({ id: saved.id, scheduledAt: when === 'later' ? sendAt!.toISOString() : null });
      message.success(when === 'later' ? `Scheduled for ${fmtDateTime(c.scheduledAt)}` : 'Sending started');
      void campaign.refetch();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };
  const confirmSend = () => {
    const m = modal.confirm({
      title: when === 'later' ? `Schedule for ${plural(eligible, 'recipient')}?` : `Send to ${plural(eligible, 'recipient')} now?`,
      content: when === 'later' ? `It goes out ${fmtDateTime(sendAt!.toISOString())}. You can cancel it until then.` : `From ${agent?.name ?? 'the sender'} with subject "${draft.subject}". This cannot be undone once delivery starts.`,
      okText: when === 'later' ? (status === 'SCHEDULED' ? 'Reschedule' : 'Schedule') : 'Send now',
      onOk: async () => {
        m.destroy();
        await doSend();
      },
    });
  };
  const sendTest = async () => {
    const saved = await save(draft);
    if (!saved) return;
    try {
      const r = await test.mutateAsync({ id: saved.id, to: testList });
      message.success(r.delivered ? `Test sent to ${r.sentTo.join(', ')}` : `Rendered "${r.subject}" (no Resend key, so nothing was delivered)`);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };
  const saveAsTemplate = async () => {
    if (!templateName?.trim()) return;
    try {
      await createTemplate.mutateAsync({ name: templateName.trim(), subject: draft.subject.trim() || draft.name || 'Untitled', design: draft.design, agentId: draft.agentId });
      message.success(`Saved as template "${templateName.trim()}"`);
      setTemplateName(null);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const steps: { icon: ReactNode; label: ReactNode }[] = [
    { icon: <EditOutlined />, label: 'Design' },
    { icon: <TeamOutlined />, label: <>Recipients <span className="campaign-step-num" style={{ width: 'auto', padding: '0 7px', borderRadius: 999 }}>{eligible.toLocaleString()}</span></> },
    { icon: <SendOutlined />, label: 'Review & send' },
  ];

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Link to="/campaigns"><Button icon={<ArrowLeftOutlined />}>Campaigns</Button></Link>
        <Input aria-label="Campaign name" size="large" value={draft.name} onChange={(e) => set({ name: e.target.value })} placeholder="Untitled campaign" style={{ flex: '1 1 280px', fontWeight: 600, fontSize: 20 }} />
        {(savedId.current || saveState !== 'idle') && <SaveIndicator state={saveState} dirty={JSON.stringify(payloadOf(draft)) !== savedJson.current} scheduledAt={status === 'SCHEDULED' ? campaign.data?.scheduledAt : null} />}
        <Button icon={<SaveOutlined />} onClick={() => setTemplateName(draft.name || draft.subject || '')}>Save as template</Button>
      </div>

      <nav className="campaign-steps" aria-label="Campaign steps">
        {steps.map((s, i) => (
          <button key={i} type="button" className={`campaign-step${step === i ? ' is-active' : ''}`} aria-current={step === i ? 'step' : undefined} onClick={() => setStep(i)}>
            <span className="campaign-step-num">{i + 1}</span>
            {s.icon}
            <span className="campaign-step-label">{s.label}</span>
          </button>
        ))}
      </nav>

      {step === 0 && (
        <>
          <Card style={{ marginBottom: 20 }}>
            <div className="campaign-fields">
              <div>
                <Typography.Text type="secondary">Subject line</Typography.Text>
                <Input ref={subjectRef} aria-label="Subject" size="large" value={draft.subject} onFocus={() => (lastField.current = 'subject')} onChange={(e) => set({ subject: e.target.value })} placeholder="What the inbox shows first" maxLength={500} suffix={<EmojiPicker label="Insert emoji in the subject" onPick={(emoji) => insertTag(emoji, 'subject')} />} />
              </div>
              <div>
                <Typography.Text type="secondary">Preview text</Typography.Text>
                <Input ref={preheaderRef} aria-label="Preview text" size="large" value={draft.design.preheader} onFocus={() => (lastField.current = 'preheader')} onChange={(e) => set({ design: { ...draft.design, preheader: e.target.value } })} placeholder="Optional" maxLength={300} suffix={<EmojiPicker label="Insert emoji in the preview text" onPick={(emoji) => insertTag(emoji, 'preheader')} />} />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>The grey line shown after the subject in most inboxes.</Typography.Text>
              </div>
              <div>
                <Typography.Text type="secondary">From</Typography.Text>
                <AgentSelect value={draft.agentId} onChange={(v) => set({ agentId: v })} />
              </div>
              <div>
                <Typography.Text type="secondary">Reply-to</Typography.Text>
                <Input aria-label="Reply-to" size="large" type="email" value={draft.replyTo} onChange={(e) => set({ replyTo: e.target.value })} placeholder={agent?.email ?? 'name@example.com'} maxLength={320} status={replyToInvalid ? 'error' : undefined} />
                <Typography.Text type={replyToInvalid ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>{replyToInvalid ? 'Enter a valid email address.' : 'Replies go here. Leave empty to use the sender\'s address.'}</Typography.Text>
              </div>
              {scope.enabled && (
                <div>
                  <Typography.Text type="secondary">Project</Typography.Text>
                  <ProjectSelect value={draft.projectId} onChange={pickProject} />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>The email takes this project's logo and colours.</Typography.Text>
                </div>
              )}
              <div>
                <Typography.Text type="secondary">Start from a template</Typography.Text>
                <Select allowClear aria-label="Template" loading={templates.isLoading} style={{ width: '100%' }} placeholder="Optional" value={draft.templateId ?? undefined} onChange={(v) => pickTemplate(v as string | undefined)} options={(templates.data ?? []).map((t) => ({ value: t.id, label: t.name }))} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
              <div className="merge-chips">
                <Typography.Text type="secondary" style={{ fontSize: 12, letterSpacing: '.04em' }}>INSERT:</Typography.Text>
                {MERGE_CHIPS.map((c) => (
                  <Button key={c.tag} size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => insertTag(c.tag)}>{c.label}</Button>
                ))}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>into the {lastField.current === 'subject' ? 'subject line' : 'preview text'}</Typography.Text>
              </div>
              <div style={{ maxWidth: 520 }}>
                <AiEmailButtons subject={draft.subject} design={draft.design} agentId={draft.agentId} onApply={({ subject, design }) => set({ subject, design })} />
              </div>
            </div>
          </Card>
          <EmailDesigner design={draft.design} onChange={(design) => set({ design })} agent={agent} sample={sample} brand={projectBrand(scope.enabled ? scope.byId(draft.projectId) : undefined)} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <Button type="primary" size="large" onClick={() => setStep(1)}>Next: recipients</Button>
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <AudienceEditor value={draft.audience} onChange={(audience) => set({ audience })} preview={preview.data} previewLoading={preview.isFetching} previewError={preview.isError ? preview.error : undefined} verified={verified} onVerified={set} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
            <Button size="large" onClick={() => setStep(0)}>Back to design</Button>
            <Button type="primary" size="large" onClick={() => setStep(2)}>Next: review</Button>
          </div>
        </>
      )}

      {step === 2 && (
        <div className="review-layout">
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <Typography.Text strong style={{ fontSize: 16 }}>{draft.subject.trim() ? renderMergeTags(draft.subject, { contact: sample, agent }, false) : '(no subject)'}</Typography.Text>
              <Segmented value={reviewDevice} onChange={(v) => setReviewDevice(v as 'desktop' | 'mobile')} options={[{ value: 'desktop', label: 'Desktop', icon: <DesktopOutlined /> }, { value: 'mobile', label: 'Mobile', icon: <MobileOutlined /> }]} />
            </div>
            <EmailPreview html={previewHtml(draft.design, { agent, sample })} width={reviewDevice === 'desktop' ? 600 : 375} />
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              Shown with tags filled in for {sampleContact.data ? 'the first recipient' : 'a sample person'}. Every recipient gets their own details.
            </Typography.Text>
          </div>
          <Space orientation="vertical" size={16} style={{ width: '100%' }}>
            <Card size="small" title="Recipients">
              <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.2 }}>{preview.isFetching && !preview.data ? <Spin size="small" /> : eligible.toLocaleString()}</div>
              <Typography.Text type="secondary">{eligible ? `From ${agent ? `${agent.name} <${agent.email}>` : '(no sender chosen)'}` : 'Nobody chosen yet.'}</Typography.Text>
              <div><Button type="link" style={{ padding: 0 }} onClick={() => setStep(1)}>Change recipients</Button></div>
            </Card>
            <Card
              size="small"
              title="Checks"
              extra={<Button type="link" size="small" onClick={() => { void preview.refetch(); void firstRecipient.refetch(); }}>Check again</Button>}
              style={problems.length ? { background: palette.errorSoft, borderColor: '#F8C9CB' } : { background: palette.successSoft, borderColor: '#B7EBCB' }}
            >
              {problems.length ? (
                <>
                  <Typography.Text type="danger" strong><ExclamationCircleOutlined /> Fix these before it can be sent:</Typography.Text>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 20, color: palette.error }}>
                    {problems.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                </>
              ) : (
                <Typography.Text style={{ color: '#067647' }}><CheckOutlined /> Ready to send.</Typography.Text>
              )}
              {!draft.design.preheader.trim() && !problems.length && <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}><WarningOutlined /> Tip: add preview text so the inbox shows more than the subject.</Typography.Text>}
            </Card>
            {/* above the test card so the send time is on screen without scrolling on a laptop */}
            <Card size="small" title="When">
              <Radio.Group value={when} onChange={(e) => { setWhen(e.target.value); if (e.target.value === 'later' && !sendAt) setSendAt(dayjs().add(1, 'hour').startOf('hour')); }} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Radio value="now">Send now</Radio>
                <Radio value="later">Schedule for later</Radio>
              </Radio.Group>
              {when === 'later' && (
                <div style={{ marginTop: 8 }}>
                  <DatePicker aria-label="Send at" showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" value={sendAt} onChange={setSendAt} style={{ width: '100%' }} disabledDate={(d) => d.isBefore(dayjs(), 'day')} status={scheduleInvalid ? 'error' : undefined} />
                  {scheduleInvalid && <Typography.Text type="danger" style={{ fontSize: 12 }}>Pick a time in the future.</Typography.Text>}
                  <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>Groups are checked again at send time, so people added before then are included.</Typography.Text>
                </div>
              )}
              <Button type="primary" size="large" block icon={<SendOutlined />} style={{ marginTop: 16 }} disabled={problems.length > 0 || preview.isFetching || scheduleInvalid} loading={send.isPending} onClick={confirmSend}>
                {when === 'later' ? (status === 'SCHEDULED' ? 'Reschedule' : 'Schedule') : `Send to ${plural(eligible, 'recipient')}`}
              </Button>
              {status === 'SCHEDULED' && <Alert style={{ marginTop: 12 }} type="info" showIcon title={`Scheduled for ${fmtDateTime(campaign.data?.scheduledAt)}. Changes save automatically and go out with it.`} />}
            </Card>
            <Card size="small" title={<Space><ExperimentOutlined />Send a test</Space>}>
              <Space.Compact style={{ width: '100%' }}>
                <Input aria-label="Test recipients" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@yourcompany.com" status={testTo && testInvalid ? 'error' : undefined} />
                <Tooltip title={!agent ? 'Choose who the email is from first' : !draft.subject.trim() ? 'Add a subject first' : undefined}>
                  <Button icon={<SendOutlined />} loading={test.isPending} disabled={testInvalid || !agent || !draft.subject.trim() || !draft.design.blocks.length} onClick={() => void sendTest()}>Test</Button>
                </Tooltip>
              </Space.Compact>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Up to 5 addresses. Tags are filled with that person's details if they're in the CRM, else a sample contact's.</Typography.Text>
            </Card>
          </Space>
        </div>
      )}

      <Modal title="Save as template" open={templateName !== null} onCancel={() => setTemplateName(null)} okText="Save template" okButtonProps={{ disabled: !templateName?.trim() }} confirmLoading={createTemplate.isPending} onOk={() => void saveAsTemplate()}>
        <Typography.Paragraph type="secondary">Saves the subject and design so you can start other campaigns from it.</Typography.Paragraph>
        <Input aria-label="Template name" autoFocus value={templateName ?? ''} onChange={(e) => setTemplateName(e.target.value)} placeholder="Template name" onPressEnter={() => void saveAsTemplate()} />
      </Modal>
    </div>
  );
}

function campaignPayload(d: Draft, withProject: boolean) {
  return { name: d.name.trim() || 'Untitled campaign', agentId: d.agentId, templateId: d.templateId, subject: d.subject, design: d.design, audience: d.audience, requireVerified: d.requireVerified, includeUnconfirmed: d.includeUnconfirmed, replyTo: replyToPayload(d.replyTo), ...(withProject && { projectId: d.projectId }) };
}


function SaveIndicator({ state, dirty, scheduledAt }: { state: SaveState; dirty: boolean; scheduledAt?: string | null }) {
  const text = state === 'saving' ? 'Saving…' : state === 'error' ? "Couldn't save" : dirty ? 'Unsaved changes' : state === 'saved' || !dirty ? 'Draft saved' : '';
  return (
    <Space size={6} style={{ color: state === 'error' ? palette.error : palette.textSecondary, fontSize: 14 }}>
      {state === 'saving' ? <Spin size="small" /> : state === 'error' ? <ExclamationCircleOutlined /> : !dirty ? <CheckOutlined /> : null}
      {text}
      {scheduledAt && <Typography.Text type="secondary">· scheduled {fmtDateTime(scheduledAt)}</Typography.Text>}
    </Space>
  );
}

/** empty = the sender's address; a half-typed address is not saved, so the stored one stays */
function replyToPayload(v: string) {
  const t = v.trim();
  if (!t) return null;
  return EMAIL_RE.test(t) ? t : undefined;
}
